/**
 * Extracts audit display fields from an entity payload.
 *
 * The backend supplies display name strings (`createdByUser`, `updatedByUser`)
 * directly on the entity — no secondary user lookups are needed.
 *
 * Legacy lifecycle / workflow-rule payloads may use `createdByName` /
 * `updatedByName`; those are accepted as a fallback.
 *
 * Empty strings are treated as absent so callers can safely use the values
 * with `|| <EmptyValue />`.
 */
export function getEntityAuditDisplayFields(source: unknown): {
	createdAt: string | null | undefined;
	updatedAt: string | null | undefined;
	createdByUser: string | null | undefined;
	updatedByUser: string | null | undefined;
} {
	if (!source || typeof source !== "object") {
		return {
			createdAt: undefined,
			updatedAt: undefined,
			createdByUser: undefined,
			updatedByUser: undefined,
		};
	}

	const record = source as Record<string, unknown>;

	const str = (v: unknown): string | null | undefined => {
		if (v === null) return null;
		if (typeof v === "string") return v.trim() || undefined;
		return undefined;
	};

	return {
		createdAt: str(record.createdAt),
		updatedAt: str(record.updatedAt),
		// Primary: API field `createdByUser` / fallback: legacy `createdByName`
		createdByUser: str(record.createdByUser) ?? str(record.createdByName),
		updatedByUser: str(record.updatedByUser) ?? str(record.updatedByName),
	};
}
